import React from "react";
import InViewReveal from "./InViewReveal";

/**
 * Trust strip - sandwiched between the hero and the about. Reads like a
 * row of verified logos on a customs declaration.
 */

// `verifyUrl` opens the issuing body's public registry. NN/g research:
// outbound links to third-party authorities are one of the strongest
// credibility signals - far more trusted than self-asserted claims.
const CERTS = [
  {
    title: "FSSAI",
    sub: "Licensed exporter, India",
    logo: "/certifications/fssai.png",
    alt: "FSSAI logo",
    logoClass: "cert-logo--wide",
    verifyUrl: "https://foscos.fssai.gov.in/",
  },
  {
    title: "APEDA",
    sub: "Agri-export council registered",
    logo: "/certifications/apeda.jpg",
    alt: "APEDA logo",
    logoClass: "cert-logo--tall",
    verifyUrl: "https://apeda.gov.in/apedawebsite/",
  },
  {
    title: "ISO 22000",
    sub: "Food safety management",
    logo: "/certifications/iso.svg",
    alt: "ISO logo",
    logoClass: "cert-logo--square",
    verifyUrl: "https://www.iso.org/standard/65464.html",
  },
  {
    title: "Spices Board",
    sub: "Govt. of India accredited",
    logo: "/certifications/spices-board.jpg",
    alt: "Spices Board India logo",
    logoClass: "cert-logo--wide",
    verifyUrl: "https://www.indianspices.com/",
  },
  {
    title: "Maersk Partner",
    sub: "Long-haul reefer rated",
    logo: "/certifications/maersk.svg",
    alt: "Maersk logo",
    logoClass: "cert-logo--wide",
    verifyUrl: "https://www.maersk.com/",
  },
];

export default function CertificationsStrip() {
  return (
    <section
      id="certifications"
      className="paper relative"
      style={{
        borderTop: "1px solid var(--rule)",
        borderBottom: "1px solid var(--rule)",
      }}
    >
      {/* Decorative engraved rule across the top */}
      <div
        className="absolute top-0 left-0 right-0 mx-auto"
        style={{
          maxWidth: 320,
          height: 12,
          transform: "translateY(-50%)",
          background: "var(--paper-1)",
        }}
      >
        <div className="rule-engraved" style={{ height: 12 }} />
      </div>

      <div className="px-6 sm:px-10 py-12 md:py-16">

        <div className="flex items-baseline justify-between gap-4 mb-10 flex-wrap">
          <div className="flex items-baseline gap-3">
            <span className="brass-ribbon">Certified</span>
            <span className="folio">CO-SIGNED · CLEARED · ON FILE</span>
          </div>
          <div className="meta hidden sm:block">FIVE BODIES · SEVEN YEARS · ON RECORD</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-10">
          {CERTS.map((c, i) => (
            <InViewReveal
              key={c.title}
              delay={Math.min(i + 1, 4)}
              className="group flex flex-col items-center text-center"
            >
              <div className="cert-logo-frame">
                <img
                  src={c.logo}
                  alt={c.alt}
                  className={`cert-logo ${c.logoClass}`}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div
                className="display mt-4"
                style={{ fontSize: 21, lineHeight: 1.1, letterSpacing: "-0.005em" }}
              >
                {c.title}
              </div>
              <div className="rule mt-2 mb-2" style={{ width: 24 }} />
              <div className="meta" style={{ letterSpacing: "0.1em", fontSize: 11 }}>
                {c.sub}
              </div>
              {c.verifyUrl && (
                <a
                  href={c.verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cert-verify"
                  aria-label={`Verify ${c.title} (opens in a new tab)`}
                >
                  Verify
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 8l6-6M3.5 2H8v4.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="square"
                    />
                  </svg>
                </a>
              )}
            </InViewReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
